// Colour helpers and theme tokens for the 3D scene. Pure module (no three.js import) so it can be
// unit-tested in Node. Every colour here is an sRGB hex string; three.js converts on use.

/** Department palette (SPEC §11). Keys are what `Department.color` may contain. */
export const PALETTE = Object.freeze({
  coral: '#E8837A',
  teal: '#4FBFA8',
  sky: '#6FAFE6',
  sand: '#E6C35C',
  lavender: '#AE97DA',
  periwinkle: '#8796F2',
  sage: '#93C27F',
  peach: '#F2A277',
});

export const PALETTE_KEYS = Object.freeze(Object.keys(PALETTE));

/** « La poule de l’étage »: the chicken's colours (the theme tints them: `chickenTint`). */
export const CHICKEN_COLORS = Object.freeze({
  body: '#FAF7F1',
  tail: '#ECE5D8',
  wing: '#EFE9DE',
  comb: '#D9463B',
  beak: '#F0A23C',
  legs: '#E9A23F',
  eye: '#1A1714',
});

/** « Le chat de l’étage »: a grey and white cat (the theme tints it: `catTint`). */
export const CAT_COLORS = Object.freeze({
  fur: '#8C929A',
  white: '#F4F1EC',
  pink: '#E3A0A6',
  nose: '#D9878F',
  eye: '#C2D05C',
  pupil: '#1A1714',
});

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Parses "#rgb" or "#rrggbb" (case-insensitive).
 * @param {string} str
 * @returns {{r:number,g:number,b:number}|null} channels 0..255, or null when invalid
 */
export function parseHex(str) {
  if (typeof str !== 'string') return null;
  const s = str.trim();
  if (!HEX_RE.test(s)) return null;
  let h = s.slice(1);
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Formats channels as a lower-case "#rrggbb".
 * @param {{r:number,g:number,b:number}} c
 * @returns {string}
 */
export function toHex({ r, g, b }) {
  const f = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${f(r)}${f(g)}${f(b)}`;
}

/** True when `value` is a valid "#rgb"/"#rrggbb" string. */
export function isHex(value) {
  return parseHex(value) !== null;
}

/**
 * Resolves a department/agent colour: a palette key ("coral", case-insensitive) or a hex string.
 * Anything else resolves the fallback (itself a key or hex); an unusable fallback gives sand.
 * @param {string} value
 * @param {string} [fallback='sand']
 * @returns {string} lower-case "#rrggbb"
 */
export function resolveColor(value, fallback = 'sand') {
  const direct = resolveOne(value);
  if (direct) return direct;
  return resolveOne(fallback) || PALETTE.sand.toLowerCase();
}

function resolveOne(value) {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(PALETTE, key)) return PALETTE[key].toLowerCase();
  const c = parseHex(value);
  return c ? toHex(c) : null;
}

/**
 * Linear mix of two hex colours in sRGB space (t = 0 → a, t = 1 → b).
 * @param {string} a
 * @param {string} b
 * @param {number} t
 * @returns {string}
 */
export function mixHex(a, b, t) {
  const x = parseHex(a) || { r: 0, g: 0, b: 0 };
  const y = parseHex(b) || { r: 0, g: 0, b: 0 };
  const k = Math.max(0, Math.min(1, Number(t) || 0));
  return toHex({ r: x.r + (y.r - x.r) * k, g: x.g + (y.g - x.g) * k, b: x.b + (y.b - x.b) * k });
}

/** Mixes toward white. */
export function lighten(hex, t) {
  return mixHex(hex, '#ffffff', t);
}

/** Mixes toward black. */
export function darken(hex, t) {
  return mixHex(hex, '#000000', t);
}

/**
 * Relative luminance (0 = black, 1 = white), WCAG formula.
 * @param {string} hex
 * @returns {number}
 */
export function luminance(hex) {
  const c = parseHex(hex);
  if (!c) return 0;
  const lin = (v) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
}

/**
 * Scene tokens per theme. Light follows SPEC §11 (cream office); dark is a night office.
 * `podTint` is how much of the department colour is mixed into the pod stone; `podGlow` is a faint
 * emissive of that colour, so each department zone keeps a visible trace of its colour at night.
 */
export const THEMES = Object.freeze({
  light: Object.freeze({
    name: 'light',
    background: '#F4F1EA',
    ink: '#1D1B18',
    muted: '#7A746A',
    stone: '#ECE7DC',
    podTint: 0.13,
    podGlow: 0,
    rugMix: 0.58,
    hall: '#EEE9DF',
    walkway: '#E4D7C3',
    slab: '#F1EDE5',
    slabEdgeIdle: '#E2DACB',
    slabEdgeGlow: '#F2A93B',
    pillar: '#E9E4DA',
    wood: '#D8B389',
    deskLeg: '#EFEAE2',
    chair: '#3E3D44',
    monitor: '#2B2D34',
    keyboard: '#ECE9E3',
    screenOff: '#262A33',
    screenBg: '#1E2430',
    screenInk: '#DCE7F5',
    screenIdle: 0.0,
    screenGlow: '#9FC4FF',
    screenGlowStrength: 0.18,
    pot: '#D9C6AE',
    leaves: '#79A86B',
    leavesDark: '#5E8F57',
    lampShade: '#F6ECD9',
    lampMetal: '#3A3A40',
    lampGlow: '#FFD7A0',
    lampGlowStrength: 0.0,
    shelf: '#CFAE86',
    books: ['#E8837A', '#6FAFE6', '#E6C35C', '#93C27F', '#AE97DA', '#F2A277', '#2F3A56', '#F4F1EA'],
    glass: '#CFE6EC',
    glassOpacity: 0.2,
    glassFrame: '#B9B2A6',
    cabin: '#F8F3EA',
    cabinLight: '#FFE9C2',
    ringText: '#8A8174',
    ringLine: '#D6CEBF',
    shadow: '#6B5A45',
    shadowOpacity: 0.26,
    contact: '#5C4D3B',
    contactOpacity: 0.28,
    hemiSky: '#FFFDF9',
    hemiGround: '#E2D9CA',
    hemiIntensity: 1.6,
    sun: '#FFF7EC',
    sunIntensity: 2.35,
    shadowIntensity: 0.62,
    fill: '#E7EEFF',
    fillIntensity: 0.35,
    exposure: 1.0,
    haloStrength: 0.0,
    glyph: 'dept',
    pants: ['#3F4250', '#4A4F5E', '#5B4B3E', '#2F3342', '#6B6358'],
    chickenTint: '#FFFFFF',
    catTint: '#FFFFFF',
  }),
  dark: Object.freeze({
    name: 'dark',
    background: '#0E131D',
    ink: '#EDE9E1',
    muted: '#8D93A1',
    stone: '#30384A',
    podTint: 0.16,
    podGlow: 0.07,
    rugMix: 0.5,
    hall: '#2B3446',
    walkway: '#343B4A',
    slab: '#1C2537',
    slabEdgeIdle: '#2A3448',
    slabEdgeGlow: '#FFB35C',
    pillar: '#232C3D',
    wood: '#8A6B50',
    deskLeg: '#454D60',
    chair: '#1A1C22',
    monitor: '#15171C',
    keyboard: '#3A4254',
    screenOff: '#141821',
    screenBg: '#132033',
    screenInk: '#D5E6FF',
    screenIdle: 0.16,
    screenGlow: '#8FB8FF',
    screenGlowStrength: 0.55,
    pot: '#5E5048',
    leaves: '#3F6B4A',
    leavesDark: '#2F5539',
    lampShade: '#FFE2B0',
    lampMetal: '#16181D',
    lampGlow: '#FFB866',
    lampGlowStrength: 0.9,
    shelf: '#5C4838',
    books: ['#B8665F', '#4E7FB0', '#B49643', '#6C8E5E', '#7F6FA3', '#B87B59', '#2F3A56', '#C9C3B6'],
    glass: '#7FB2D6',
    glassOpacity: 0.16,
    glassFrame: '#3A4458',
    cabin: '#FFE6BE',
    cabinLight: '#FFC57A',
    ringText: '#8F98AB',
    ringLine: '#2E3950',
    shadow: '#000000',
    shadowOpacity: 0.42,
    contact: '#000000',
    contactOpacity: 0.45,
    hemiSky: '#B4BACB',
    hemiGround: '#231F24',
    hemiIntensity: 1.3,
    sun: '#CBD4F2',
    sunIntensity: 1.2,
    shadowIntensity: 0.7,
    fill: '#FFB870',
    fillIntensity: 0.45,
    exposure: 1.1,
    haloStrength: 1.0,
    glyph: 'light',
    pants: ['#2C3040', '#353B4C', '#3C3430', '#22252F', '#474038'],
    chickenTint: '#FFF8EC', // warm: stays white under the bluish night light
    catTint: '#FFF8EC',
  }),
});

/**
 * Returns the theme tokens for 'light' | 'dark' (anything else → light).
 * @param {string} name
 */
export function themeTokens(name) {
  return name === 'dark' ? THEMES.dark : THEMES.light;
}
